import type { HTMLAttributes } from 'react';
import { tv } from 'tailwind-variants';

const dividerStyles = tv({
  base: 'flex items-center gap-3 text-fg-subtle',
  variants: {
    spacing: { none: '', sm: 'my-4', md: 'my-6', lg: 'my-8' },
  },
  defaultVariants: { spacing: 'md' },
});

export interface DividerProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Show the active theme's ornament glyph between two hairlines. The glyph comes from the
   * `--ht-ornament-divider` token, so each game-system theme supplies its own art.
   */
  ornament?: boolean;
  spacing?: 'none' | 'sm' | 'md' | 'lg';
}

/** Horizontal separator. Use the ornament at section boundaries only (design principle 3). */
export function Divider({ ornament = false, spacing, className, ...props }: DividerProps) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: an <hr> can't contain the ornament glyph; role="separator" on a div is valid ARIA
    <div
      {...props}
      role="separator"
      aria-orientation="horizontal"
      className={dividerStyles({ spacing, className })}
    >
      <span className="h-px flex-1 bg-border" />
      {ornament ? (
        <>
          <span
            aria-hidden="true"
            className="h-4 w-6 shrink-0 bg-current"
            style={{
              maskImage: 'var(--ht-ornament-divider)',
              WebkitMaskImage: 'var(--ht-ornament-divider)',
              maskRepeat: 'no-repeat',
              maskPosition: 'center',
              maskSize: 'contain',
            }}
          />
          <span className="h-px flex-1 bg-border" />
        </>
      ) : null}
    </div>
  );
}
